import ButtonVariants from "@core-components/button/ButtonVariants";
import SSContainer from "@core-components/container/Container";
import EPublicRoutes from "@core-types/routes.enum";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { useAppRegion } from "@hooks/region/useAppRegion";
import { Burger, Collapse, Flex, Group, Stack, ThemeIcon } from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import {
  IconShieldCheck,
  IconSword,
  IconUserFilled,
  IconWorld,
} from "@tabler/icons-react";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import type { breakpoints } from "src/App";
import BasketButton from "./BasketButton";
import Discord from "./Discord";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";
import LoginNavbarButton from "./components/LoginNavbarButton";

export interface ILink {
  label: string | ReactNode;
  path?: string;
  hiddenFrom: breakpoints;
  isMenu?: boolean;
  menuItems?: {
    label: ILink["label"];
    path: ILink["path"];
    icon: ReactNode;
  }[];
}

const NavBar: React.FC = () => {
  const [isNavOpen, nav] = useDisclosure(false);
  const { t } = useAppTranslation("components.layout.navbar");
  const toggleIcon = useRef<HTMLButtonElement>(null);
  const { regionUrlBuilder, currentRegion } = useAppRegion();
  const location = useLocation();

  const ref = useClickOutside(() => {
    nav.close();
  }, ["mouseup", "touchend"]);

  useEffect(() => {
    // Close the navbar when the navigates to another page
    nav.close();
  }, [location.pathname]);

  const navbarButtons: ILink[] = useMemo(
    () => [
      {
        label: t("home"),
        path: EPublicRoutes.HOME,
        hiddenFrom: "xs",
      },
      {
        label: t("lolAccounts"),
        hiddenFrom: "sm",
        isMenu: true,
        menuItems: [
          {
            label: t("rankedAccounts"),
            path: regionUrlBuilder(EPublicRoutes.SMURFS_RANKED),
            icon: <IconSword />,
          },
          {
            label: t("normalAccounts"),
            path: regionUrlBuilder(EPublicRoutes.SMURFS_NORMAL),
            icon: <IconWorld />,
          },
          {
            label: t("handleleveledAccounts"),
            path: regionUrlBuilder(EPublicRoutes.SMURFS_HANDLEVEL),
            icon: <IconShieldCheck />,
          },
          {
            label: t("skinAccounts"),
            path: EPublicRoutes.SMURFS_SKIN,
            icon: <IconUserFilled />,
          },
        ],
      },
      {
        label: t("valorantAccounts"),
        path: regionUrlBuilder(EPublicRoutes.VALORANT),
        hiddenFrom: "md",
      },
      {
        label: t("reviews"),
        path: EPublicRoutes.REVIEWS,
        hiddenFrom: "xl",
      },
      {
        label: t("faqSupport"),
        path: EPublicRoutes.FAQ,
        hiddenFrom: "xl",
      },
    ],
    [t, currentRegion],
  );

  return (
    <div
      ref={ref}
      className="bg-primary-default/50 px-2 xs:px-3 py-4 text-white shadow-2xl backdrop-blur-md sm:px-2 md:px-5"
    >
      <SSContainer className="gap-0 p-0">
        <Group gap={0} h={35} justify="space-between" wrap="nowrap">
          <Logo />

          <Group gap={"1rem"} wrap="nowrap">
            {navbarButtons.map((props) => (
              <ButtonVariants.Navbar {...props} key={props.label?.toString()} />
            ))}
            <Discord iconVisibleFrom="lg" textVisibleFrom="lg" />
          </Group>

          <Group className="flex-nowrap gap-3">
            <BasketButton />
            <LanguageSwitcher />
            <LoginNavbarButton />
            <ThemeIcon
              className="default-icon hover-scale xl:hidden"
              radius={12}
              size={50}
            >
              <Burger
                aria-label={t("burgerAriaLabel")}
                opened={isNavOpen}
                onClick={nav.toggle}
                ref={toggleIcon}
                hiddenFrom="xl"
              />
            </ThemeIcon>
          </Group>
        </Group>

        <Collapse
          className={"z-40 w-full rounded bg-inherit pl-0"}
          in={isNavOpen}
        >
          <Stack
            align="start"
            gap={5}
            justify="flex-start"
            p={0}
            w={"100%"}
            className="mt-3"
          >
            {navbarButtons.map((props) => (
              <ButtonVariants.NavbarResponsive
                {...props}
                key={props.label?.toString()}
              />
            ))}
            <Flex hiddenFrom="lg">
              <Discord />
            </Flex>
          </Stack>
        </Collapse>
      </SSContainer>
    </div>
  );
};

export default NavBar;
